import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase, isNative } from '../lib/supabase'
import { AlertCircle, LogIn, UserPlus, Mail, KeyRound, CheckCircle } from 'lucide-react'
import { GoogleIcon, AppleIcon } from './SocialIcons'
import { validatePassword } from '../utils/passwordValidation'
import { checkRateLimit, recordAttempt } from '../utils/authRateLimit'
import { checkDisposableEmail } from '../utils/disposableEmails'
import TurnstileWidget from './TurnstileWidget'
import logger from '../utils/logger'
import logoFull from '../assets/logo-full.png'
import './Modal.css'

const OTP_LENGTH = 6

export default function LoginScreen() {
  const { signIn, signUp, signInWithProvider, resetPassword, updatePassword, passwordRecovery, clearPasswordRecovery } = useAuth()
  const [mode, setMode] = useState('signin') // 'signin' | 'signup' | 'forgot' | 'reset'
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [passwordErrors, setPasswordErrors] = useState([])
  const [resetEmailSent, setResetEmailSent] = useState(false)
  const [signupEmailSent, setSignupEmailSent] = useState(false)
  const [captchaToken, setCaptchaToken] = useState(null)
  const [otpCode, setOtpCode] = useState(Array(OTP_LENGTH).fill(''))
  const [otpError, setOtpError] = useState(null)
  const [otpVerifying, setOtpVerifying] = useState(false)
  const [otpResent, setOtpResent] = useState(false)
  const otpRefs = useRef([])

  // When Supabase detects a recovery token in the URL, switch to reset mode
  useEffect(() => {
    if (passwordRecovery) {
      setMode('reset')
      setError(null)
      setPassword('')
      setConfirmPassword('')
    }
  }, [passwordRecovery])

  // Live password validation feedback for signup/reset
  useEffect(() => {
    if ((mode === 'signup' || mode === 'reset') && password) {
      const { errors } = validatePassword(password)
      setPasswordErrors(errors)
    } else {
      setPasswordErrors([])
    }
  }, [password, mode])

  const handleSocialLogin = async (provider) => {
    setError(null)
    setLoading(true)
    const { error: socialError } = await signInWithProvider(provider)
    if (socialError) {
      logger.capture(socialError, { source: 'LoginScreen', action: 'socialLogin', provider })
      setError(socialError.message)
    }
    setLoading(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    // Rate limit check
    const { allowed, waitSeconds } = checkRateLimit()
    if (!allowed) {
      setError(`Too many attempts. Please wait ${waitSeconds}s and try again.`)
      setLoading(false)
      return
    }

    if (mode === 'forgot') {
      if (!email.trim()) return setLoading(false)
      recordAttempt()
      const { error: resetError } = await resetPassword(email.trim())
      if (resetError) {
        logger.capture(resetError, { source: 'LoginScreen', action: 'resetPassword' })
        setError(resetError.message)
      } else {
        setResetEmailSent(true)
      }
      setLoading(false)
      return
    }

    if (mode === 'reset') {
      const { valid, errors } = validatePassword(password)
      if (!valid) {
        setError(errors[0])
        setLoading(false)
        return
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match')
        setLoading(false)
        return
      }
      const { error: updateError } = await updatePassword(password)
      if (updateError) {
        logger.capture(updateError, { source: 'LoginScreen', action: 'updatePassword' })
        setError(updateError.message)
      }
      // On success, passwordRecovery clears and user is signed in automatically
      setLoading(false)
      return
    }

    if (!email.trim() || !password) return setLoading(false)
    if (mode === 'signup' && (!firstName.trim() || !lastName.trim())) return setLoading(false)

    if (mode === 'signup') {
      // Block disposable/throwaway email domains
      const { allowed: emailAllowed, reason: emailReason } = checkDisposableEmail(email.trim())
      if (!emailAllowed) {
        setError(emailReason)
        setLoading(false)
        return
      }

      const { valid, errors } = validatePassword(password)
      if (!valid) {
        setError(errors[0])
        setLoading(false)
        return
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match')
        setLoading(false)
        return
      }
    }

    recordAttempt()
    const { error: authError } = mode === 'signup'
      ? await signUp(email.trim(), password, firstName.trim(), lastName.trim(), captchaToken)
      : await signIn(email.trim(), password)
    if (authError) {
      logger.auth(`Auth error (${mode}):`, { error: authError.message })
      logger.capture(authError, { source: 'LoginScreen', action: mode })
      setError(authError.message)
    } else if (mode === 'signup') {
      // With email confirmation enabled, Supabase doesn't create a session until verified.
      // Show confirmation screen so the user knows to check their email.
      setSignupEmailSent(true)
    }

    setLoading(false)
  }

  const switchMode = (newMode) => {
    setMode(newMode)
    setError(null)
    setPasswordErrors([])
    setResetEmailSent(false)
    setSignupEmailSent(false)
    setFirstName('')
    setLastName('')
    setPassword('')
    setConfirmPassword('')
    setCaptchaToken(null)
    setOtpCode(Array(OTP_LENGTH).fill(''))
    setOtpError(null)
  }

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1)
    const newCode = [...otpCode]
    newCode[index] = digit
    setOtpCode(newCode)
    setOtpError(null)
    if (digit && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus()
    }
    if (digit && index === OTP_LENGTH - 1 && newCode.every(d => d)) {
      handleOtpVerify(newCode.join(''))
    }
  }

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpRefs.current[index - 1]?.focus()
    }
  }

  const handleOtpPaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    const newCode = [...otpCode]
    for (let i = 0; i < OTP_LENGTH; i++) {
      newCode[i] = pasted[i] || ''
    }
    setOtpCode(newCode)
    setOtpError(null)
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1)
    otpRefs.current[focusIndex]?.focus()
    if (pasted.length === OTP_LENGTH) {
      handleOtpVerify(pasted)
    }
  }

  const handleOtpVerify = async (token) => {
    setOtpVerifying(true)
    setOtpError(null)
    const { error: verifyError } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token,
      type: 'email',
    })
    if (verifyError) {
      logger.capture(verifyError, { source: 'LoginScreen', action: 'verifyOtp' })
      setOtpError('Invalid or expired code. Please try again.')
      setOtpCode(Array(OTP_LENGTH).fill(''))
      otpRefs.current[0]?.focus()
    }
    // On success, Supabase creates the session and AuthContext picks it up automatically
    setOtpVerifying(false)
  }

  const handleOtpResend = async () => {
    setOtpError(null)
    await supabase.auth.resend({ type: 'signup', email: email.trim() })
    setOtpResent(true)
    setTimeout(() => setOtpResent(false), 30000)
  }

  const linkStyle = {
    background: 'none', border: 'none', color: 'var(--accent-primary)',
    cursor: 'pointer', padding: 0, fontSize: 'inherit', fontWeight: '600',
  }

  const socialBtnStyle = {
    width: '100%',
    height: '44px',
    borderRadius: '9999px',
    fontSize: '0.9375rem',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.625rem',
    cursor: loading ? 'wait' : 'pointer',
    transition: 'background-color 0.15s',
    border: '1px solid var(--border-color)',
  }

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'calc(2rem + env(safe-area-inset-top, 0px)) 1.25rem 2rem',
      backgroundColor: 'var(--bg-primary)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        textAlign: 'center',
      }}>
        {/* Brand */}
        <img src={logoFull} alt="Press2DN" style={{ maxWidth: '200px', width: '100%', height: 'auto', marginBottom: '0.5rem' }} />
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '1rem',
          marginBottom: '2.5rem',
        }}>
          Built to track the real game
        </p>

        <form onSubmit={handleSubmit} style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '0.75rem',
            padding: '2rem 1.5rem',
            border: '1px solid var(--border-color)',
          }}>

            {/* Reset password form (from recovery link) */}
            {mode === 'reset' && (
              <>
                <p style={{
                  color: 'var(--text-primary)',
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  marginBottom: '0.5rem',
                }}>Set new password</p>
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.875rem',
                  marginBottom: '1.5rem',
                }}>Enter your new password below.</p>

                <div className="form-group">
                  <label style={{ textAlign: 'left' }}>New password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="New password"
                    autoComplete="new-password"
                    autoFocus
                    required
                  />
                </div>

                {password && passwordErrors.length > 0 && (
                  <div style={{
                    textAlign: 'left',
                    marginTop: '-0.5rem',
                    marginBottom: '0.75rem',
                    fontSize: '0.75rem',
                    color: '#ef4444',
                    lineHeight: 1.5,
                  }}>
                    {passwordErrors.map((err) => (
                      <div key={err}>{err}</div>
                    ))}
                  </div>
                )}

                <div className="form-group">
                  <label style={{ textAlign: 'left' }}>Confirm password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm password"
                    autoComplete="new-password"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading || !password || !confirmPassword}
                  style={{
                    width: '100%',
                    opacity: loading || !password || !confirmPassword ? 0.7 : 1,
                    cursor: loading ? 'wait' : 'pointer',
                  }}
                >
                  <KeyRound size={18} />
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </>
            )}

            {/* Forgot password form */}
            {mode === 'forgot' && !resetEmailSent && (
              <>
                <p style={{
                  color: 'var(--text-primary)',
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  marginBottom: '0.5rem',
                }}>Reset password</p>
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.875rem',
                  marginBottom: '1.5rem',
                }}>Enter your email and we'll send you a reset link.</p>

                <div className="form-group">
                  <label style={{ textAlign: 'left' }}>Email address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    autoFocus
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading || !email.trim()}
                  style={{
                    width: '100%',
                    opacity: loading || !email.trim() ? 0.7 : 1,
                    cursor: loading ? 'wait' : 'pointer',
                  }}
                >
                  <Mail size={18} />
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </>
            )}

            {/* Reset email sent confirmation */}
            {mode === 'forgot' && resetEmailSent && (
              <div style={{ padding: '1rem 0' }}>
                <CheckCircle size={40} style={{ color: 'var(--accent-primary)', marginBottom: '1rem' }} />
                <p style={{
                  color: 'var(--text-primary)',
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  marginBottom: '0.5rem',
                }}>Check your email</p>
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.875rem',
                }}>
                  We sent a password reset link to <strong style={{ color: 'var(--text-primary)' }}>{email}</strong>. Click the link in the email to set a new password.
                </p>
              </div>
            )}

            {/* Signup OTP verification */}
            {mode === 'signup' && signupEmailSent && (
              <div style={{ padding: '0.5rem 0' }}>
                <Mail size={36} style={{ color: 'var(--accent-primary)', marginBottom: '0.75rem' }} />
                <p style={{
                  color: 'var(--text-primary)',
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  marginBottom: '0.5rem',
                }}>Enter verification code</p>
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.875rem',
                  marginBottom: '1.25rem',
                }}>
                  We sent a code to <strong style={{ color: 'var(--text-primary)' }}>{email}</strong>
                </p>

                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '0.375rem',
                  marginBottom: '1rem',
                }}>
                  {otpCode.map((digit, i) => (
                    <input
                      key={i}
                      ref={el => otpRefs.current[i] = el}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleOtpChange(i, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(i, e)}
                      onPaste={i === 0 ? handleOtpPaste : undefined}
                      disabled={otpVerifying}
                      autoFocus={i === 0}
                      style={{
                        width: '38px',
                        height: '48px',
                        textAlign: 'center',
                        fontSize: '1.375rem',
                        fontWeight: '700',
                        borderRadius: '0.5rem',
                        border: `1px solid ${otpError ? '#ef4444' : digit ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                        backgroundColor: 'var(--bg-tertiary)',
                        color: 'var(--text-primary)',
                        outline: 'none',
                        opacity: otpVerifying ? 0.7 : 1,
                      }}
                    />
                  ))}
                </div>

                {otpError && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.375rem',
                    color: '#ef4444',
                    fontSize: '0.8125rem',
                    marginBottom: '0.75rem',
                  }}>
                    <AlertCircle size={14} />
                    <span>{otpError}</span>
                  </div>
                )}

                {otpVerifying && (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginBottom: '0.75rem' }}>
                    Verifying...
                  </p>
                )}

                <div>
                  {otpResent ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.375rem',
                      color: 'var(--accent-primary)',
                      fontSize: '0.8125rem',
                      fontWeight: '600',
                    }}>
                      <CheckCircle size={14} />
                      <span>New code sent</span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleOtpResend}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--accent-primary)',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontSize: '0.8125rem',
                        padding: 0,
                      }}
                    >
                      Didn't get it? Resend code
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Sign in / Sign up forms */}
            {(mode === 'signin' || mode === 'signup') && !signupEmailSent && (
              <>
                {/* Social login buttons */}
                <button
                  type="button"
                  onClick={() => handleSocialLogin('google')}
                  disabled={loading}
                  style={{
                    ...socialBtnStyle,
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    marginBottom: '0.625rem',
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  <GoogleIcon size={20} />
                  Continue with Google
                </button>

                <button
                  type="button"
                  onClick={() => handleSocialLogin('apple')}
                  disabled={loading}
                  style={{
                    ...socialBtnStyle,
                    backgroundColor: '#000',
                    color: '#fff',
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  <AppleIcon size={20} />
                  Continue with Apple
                </button>

                {/* Divider */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  margin: '1.25rem 0',
                }}>
                  <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
                  <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: '500' }}>or</span>
                  <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
                </div>

                {mode === 'signup' && (
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label style={{ textAlign: 'left' }}>First Name</label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="First"
                        autoComplete="given-name"
                        autoFocus
                        required
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label style={{ textAlign: 'left' }}>Last Name</label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Last"
                        autoComplete="family-name"
                        required
                      />
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label style={{ textAlign: 'left' }}>Email address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    autoFocus={mode === 'signin'}
                    required
                  />
                </div>

                <div className="form-group">
                  <label style={{ textAlign: 'left' }}>Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                    required
                  />
                </div>

                {mode === 'signup' && password && passwordErrors.length > 0 && (
                  <div style={{
                    textAlign: 'left',
                    marginTop: '-0.5rem',
                    marginBottom: '0.75rem',
                    fontSize: '0.75rem',
                    color: '#ef4444',
                    lineHeight: 1.5,
                  }}>
                    {passwordErrors.map((err) => (
                      <div key={err}>{err}</div>
                    ))}
                  </div>
                )}

                {mode === 'signin' && (
                  <div style={{ textAlign: 'right', marginTop: '-0.5rem', marginBottom: '1rem' }}>
                    <button type="button" onClick={() => switchMode('forgot')} style={{
                      ...linkStyle, fontSize: '0.8125rem', fontWeight: '500',
                    }}>Forgot password?</button>
                  </div>
                )}

                {mode === 'signup' && (
                  <div className="form-group">
                    <label style={{ textAlign: 'left' }}>Confirm password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm password"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                )}

                {mode === 'signup' && (
                  <TurnstileWidget
                    onVerify={setCaptchaToken}
                    theme="auto"
                  />
                )}

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading || !email.trim() || !password || (mode === 'signup' && !captchaToken && !isNative && !!import.meta.env.VITE_TURNSTILE_SITE_KEY)}
                  style={{
                    width: '100%',
                    opacity: loading || !email.trim() || !password || (mode === 'signup' && !captchaToken && !isNative && !!import.meta.env.VITE_TURNSTILE_SITE_KEY) ? 0.7 : 1,
                    cursor: loading ? 'wait' : 'pointer',
                  }}
                >
                  {mode === 'signin' ? <LogIn size={18} /> : <UserPlus size={18} />}
                  {loading
                    ? (mode === 'signin' ? 'Signing in...' : 'Signing up...')
                    : (mode === 'signin' ? 'Sign In' : 'Sign Up')
                  }
                </button>
              </>
            )}

            {error && (
              <div className="login-error">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            {/* Footer links */}
            <p style={{
              marginTop: '1.25rem',
              fontSize: '0.875rem',
              color: 'var(--text-secondary)',
            }}>
              {mode === 'signin' && (
                <>Don't have an account?{' '}
                  <button type="button" onClick={() => switchMode('signup')} style={linkStyle}>Sign up</button>
                </>
              )}
              {mode === 'signup' && (
                <>Already have an account?{' '}
                  <button type="button" onClick={() => switchMode('signin')} style={linkStyle}>Sign in</button>
                </>
              )}
              {(mode === 'forgot' || mode === 'reset') && (
                <>Back to{' '}
                  <button type="button" onClick={() => { switchMode('signin'); clearPasswordRecovery() }} style={linkStyle}>Sign in</button>
                </>
              )}
            </p>
          </form>

        <p style={{
          marginTop: '1.5rem',
          fontSize: '0.75rem',
          color: 'var(--text-muted)',
        }}>
          <a href="https://app.press2dn.com/privacy" onClick={(e) => { e.preventDefault(); import('../utils/openExternalUrl').then(m => m.openExternalUrl('https://app.press2dn.com/privacy')) }} style={{ color: 'var(--text-muted)', textDecoration: 'underline', cursor: 'pointer' }}>Privacy Policy</a>
          {' · '}
          <a href="https://app.press2dn.com/terms" onClick={(e) => { e.preventDefault(); import('../utils/openExternalUrl').then(m => m.openExternalUrl('https://app.press2dn.com/terms')) }} style={{ color: 'var(--text-muted)', textDecoration: 'underline', cursor: 'pointer' }}>Terms of Service</a>
        </p>
      </div>
    </div>
  )
}
